import React, { forwardRef } from 'react'
import classNames from 'classnames'
import { NavLink, NavLinkProps, useLocation } from 'react-router-dom'

import { ButtonUnstyled } from 'components/Button'
import Tooltip from 'components/Tooltip'
import Dropdown from 'components/Dropdown'

import { useIsNodeContentTruncated } from 'utils/hooks/useIsNodeContentTruncated'
import useHover from 'utils/hooks/useHover'

import {
  MenuLink,
  Icon,
  ActionWrapper,
  Text,
  DropdownAction,
  EllipsisIcon,
} from './Item.styles'

import {
  useMenuShareDrop,
  ShareDropPayload,
} from '../MenuLink/useMenuShareDrop'

interface CommonProps {
  active?: boolean
  action?: React.ReactNode
  className?: string
  iconClassName?: string
  forceHover?: boolean
  icon?: React.ElementType
  text: React.ReactNode
  additional?: React.ReactNode
}

export const BaseButtonItem: React.FC<ButtonItemProps> = React.forwardRef(
  function BaseButtonItem(props, ref) {
    const {
      active,
      action,
      additional,
      className,
      iconClassName,
      forceHover,
      icon,
      text,

      ...buttonProps
    } = props

    return (
      <MenuLink
        ref={ref}
        as={ButtonUnstyled}
        aria-label={text}
        className={classNames(className, { active })}
        $forceHover={forceHover}
        {...buttonProps}>
        {icon && <Icon className={iconClassName} as={icon} role="image" />}
        <Text>{text}</Text>
        {action && <ActionWrapper>{action}</ActionWrapper>}
        {additional}
      </MenuLink>
    )
  }
)

type ButtonItemProps = React.ButtonHTMLAttributes<any> & CommonProps

export const ButtonItem: React.FC<ButtonItemProps> = props => {
  const {
    action,
    additional,
    className,
    iconClassName,
    forceHover,
    icon,
    text,

    ...buttonProps
  } = props

  const [isNodeContentTruncated, truncatedNodeRef] = useIsNodeContentTruncated()

  return (
    <Tooltip
      placement="right"
      content={text}
      disabled={!isNodeContentTruncated}>
      {props => (
        <BaseButtonItem
          ref={truncatedNodeRef}
          action={action}
          additional={additional}
          className={className}
          iconClassName={iconClassName}
          forceHover={forceHover}
          icon={icon}
          text={text}
          {...buttonProps}
          {...props}
        />
      )}
    </Tooltip>
  )
}

type LinkItemProps = NavLinkProps &
  CommonProps & {
    $activeIcon?: React.ElementType
  }

export const LinkItem = forwardRef((props: LinkItemProps, ref) => {
  const {
    action,
    additional,
    className,
    iconClassName,
    forceHover,
    icon: iconComponent,
    $activeIcon,
    text,
    ...linkProps
  } = props

  /* This additional connect to the react-router-context would be unnecessary using the "component" prop */
  /* But react-router doesn't export the "LinkAnchor" component */
  const location = useLocation()
  const icon =
    (location.pathname === linkProps.to && $activeIcon) || iconComponent

  const [isNodeContentTruncated, truncatedNodeRef] = useIsNodeContentTruncated()
  const [hovered, hoverEventListeners] = useHover()

  return (
    <Tooltip
      placement="right"
      content={text}
      disabled={!isNodeContentTruncated || hovered}>
      <MenuLink
        as={NavLink}
        activeClassName="active"
        className={className}
        $forceHover={forceHover}
        ref={ref as React.MutableRefObject<any>}
        {...linkProps}>
        {icon && <Icon className={iconClassName} as={icon} />}
        <Text ref={truncatedNodeRef}>{text}</Text>
        {action && (
          <ActionWrapper {...hoverEventListeners}>{action}</ActionWrapper>
        )}
        {additional}
      </MenuLink>
    </Tooltip>
  )
})

LinkItem.displayName = 'LinkItem'

type ExternalLinkItemProps = React.ComponentProps<'a'> & CommonProps
export const ExternalLinkItem: React.FC<ExternalLinkItemProps> = props => {
  const {
    action,
    additional,
    iconClassName,
    forceHover,
    icon,
    text,
    ...anchorProps
  } = props

  const [isNodeContentTruncated, truncatedNodeRef] = useIsNodeContentTruncated()

  return (
    <Tooltip
      placement="right"
      content={text}
      disabled={!isNodeContentTruncated}>
      {props => (
        <MenuLink
          as="a"
          rel="noopener noreferrer"
          target="_blank"
          $forceHover={forceHover}
          {...anchorProps}
          {...props}>
          {icon && <Icon className={iconClassName} as={icon} />}
          <Text ref={truncatedNodeRef}>{text}</Text>
          {action && <ActionWrapper>{action}</ActionWrapper>}
          {additional}
        </MenuLink>
      )}
    </Tooltip>
  )
}

type DroppableLinkItemProps = LinkItemProps & {
  onDropped: (data: ShareDropPayload) => void
}
export const DroppableLinkItem = forwardRef(
  (props: DroppableLinkItemProps, ref) => {
    const { onDropped, ...linkProps } = props
    const { isDraggedOver, kind, ...dragEvents } = useMenuShareDrop({
      onDropped,
    })

    return (
      <LinkItem
        forceHover={isDraggedOver}
        ref={ref}
        {...linkProps}
        {...dragEvents}
      />
    )
  }
)

interface ActionDropdownProps {
  text: string
  content: React.ReactNode
}

export const ActionDropdown: React.FC<ActionDropdownProps> = props => {
  const { content, text } = props
  return (
    <Dropdown
      toggle={props => (
        <DropdownAction data-testid="project-options" {...props}>
          <EllipsisIcon />
          <span className="sr-only">{text}</span>
        </DropdownAction>
      )}
      placement="bottom"
      usePortal={true}>
      {content}
    </Dropdown>
  )
}
